import { useAuth } from '@clerk/nextjs';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import { hasSubscription } from '@web/guards/Subscription';
import { App } from '@web/query/App';
import axios from 'axios';
import React from 'react';
export interface ApiResponse {
  success: true;
  url: string;
  returnUrl: string;
}

export interface Failure {
  message: string;
}

export interface Params {
  totalCredits: number;
  amount: number;
}

/**
 * React Query hook to fetch all the available useAddonCreditsPaymentIntent for the user from stripe
 * @returns ApiResponse | undefined
 */
const useAddonCreditsPaymentIntent = ({ totalCredits, amount }: Params) => {
  const { orgId } = useAuth();
  const { data: addonCreditData } = App.Supabase.useAddonCreditPlans();
  const { data: subscriptionData } = App.useSubscriptions();

  const subscription = React.useMemo(() => {
    if (hasSubscription(subscriptionData)) {
      return subscriptionData?.subscription;
    }

    return undefined;
  }, [subscriptionData]);

  return useQuery<ApiResponse, Failure>({
    queryKey: [Query.StripeKeys.addonCreditsPaymentIntent, { totalCredits, amount }],
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    enabled: false,
    queryFn: async () => {
      const currency: AvailableCurrencies = subscription?.billingCurrency ?? 'AUD';

      const response = await axios.post<ApiResponse>(
        `${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/customer/${orgId}/addon-credits-payment-intent`,
        {
          totalCredits,
          amount,
          pricePerCredit: addonCreditData?.price_per_credit,
          priceId: addonCreditData?.stripe_price_id,
          currency,
        },
      );
      return response.data;
    },
  });
};

export { useAddonCreditsPaymentIntent };
